import {getActiveIframe} from '@packages/systems/core/helpers/SiteIframeMigrationHelper';
import type {
  Interaction,
  Timeline,
  TimelineId,
} from '@packages/systems/ix3/core';
import type {IX3} from '@packages/systems/ix3/runtime';
import {getFeatureFlagOutsideOfReact} from '@packages/systems/feature-config/client';
import {FEATURE_FLAGS} from '@packages/systems/feature-config/shared';

declare global {
  interface Window {
    __wf_ix3?: boolean;
    __wf_ix3_continuous_preview?: boolean;
    __wf_ix3_marker_focus?: string;
  }
}

export const IX3_READY_EVENT = '__wf_ix3_ready';

/**
 * A fresh tuple per trigger, as a published page's parsed JSON gives the
 * runtime. Exit intent and Duration key their one-shot state on the tuple
 * object (`PageLevelTriggerState`, a WeakMap in
 * `packages/systems/ix3/core/src/pageLevelTriggerState.ts`), assuming it belongs
 * to one registration of one interaction. The store's tuples don't: immer keeps
 * an unchanged tuple identical across registrations, and a duplicated
 * interaction shares its original's. Without this, one interaction firing spent
 * its duplicate. `IX3Engine.remove()` already resets the state of every trigger
 * it drops, so a Preview's destroy+register clears its own carry-over; this
 * covers the tuple a duplicate shares. Any caller that destroys and re-registers
 * the engine with store objects (AISB's animation-pack re-registration too)
 * goes through this.
 */
export const withOwnTriggerTuples = (
  interactions: Interaction[]
): Interaction[] =>
  interactions.map((interaction) => ({
    ...interaction,
    triggers: interaction.triggers.map(
      (trigger) => [...trigger] as typeof trigger
    ),
  }));

/**
 * Iframe bridge utilities for communicating with the IX3 runtime engine
 * running inside the designer canvas iframe.
 */
export class IX3EngineUtility {
  static getIX3RuntimeEngine(): IX3 | null {
    const iframe = getActiveIframe();
    if (!iframe?.contentWindow) return null;

    try {
      const Webflow = iframe.contentWindow.Webflow;
      if (!Webflow?.require) return null;

      const ix3Module = Webflow.require('ix3');
      return ix3Module?.getInstance() || null;
    } catch {
      return null;
    }
  }

  static setIx3Flag(value: boolean | undefined): void {
    const iframe = getActiveIframe();
    if (iframe?.contentWindow) {
      iframe.contentWindow.__wf_ix3 = value;
    }
  }

  static setContinuousPreviewFlag(enabled: boolean | undefined): void {
    const iframe = getActiveIframe();
    if (iframe?.contentWindow) {
      iframe.contentWindow.__wf_ix3_continuous_preview = enabled;
    }
  }

  /**
   * Hints the runtime which interaction was open in the Designer, so preview
   * can default that interaction's scroll-marker set to full opacity. Must be
   * set before `registerEngineState` so it is present when the runtime picks
   * its default focused set.
   */
  static setMarkerFocus(interactionId: string | undefined): void {
    const iframe = getActiveIframe();
    if (iframe?.contentWindow) {
      iframe.contentWindow.__wf_ix3_marker_focus = interactionId;
    }
  }

  static registerEngineState(
    interactions: Interaction[],
    timelines: Timeline[]
  ): void {
    const engine = IX3EngineUtility.getIX3RuntimeEngine();
    if (!engine) return;
    engine.destroy();
    // The canvas is a heavy DOM, so the first open of a modal can block the
    // main thread for 100ms+. GSAP's default smoothing only covers gaps over
    // 500ms, so a shorter one eats the start of the ::backdrop fade. This
    // sets it for the canvas iframe's GSAP only; published pages keep the default.
    getActiveIframe()?.contentWindow?.gsap?.ticker.lagSmoothing(100, 33);
    // Mirrors the publish snippet (getIx3Js) so preview plays what publishes.
    engine.setFeatures({
      timelineGroups: getFeatureFlagOutsideOfReact(
        FEATURE_FLAGS.IX3_TIMELINE_GROUPS
      ),
    });
    engine.register(withOwnTriggerTuples(interactions), timelines);
    window.dispatchEvent(new CustomEvent(IX3_READY_EVENT));
  }

  static getGsapTimeline(
    timelineId: TimelineId,
    targetElement: HTMLElement | null
  ): gsap.core.Timeline | null {
    const engine = IX3EngineUtility.getIX3RuntimeEngine();
    if (!engine) return null;
    return engine
      .getCoordinator()
      .getTimeline(timelineId, targetElement) as gsap.core.Timeline | null;
  }

  static getAllGsapTimelines(timelineId: TimelineId): gsap.core.Timeline[] {
    const engine = IX3EngineUtility.getIX3RuntimeEngine();
    if (!engine) return [];
    return engine
      .getCoordinator()
      .getAllTimelines(timelineId) as gsap.core.Timeline[];
  }
}

export const getIX3RuntimeEngine = IX3EngineUtility.getIX3RuntimeEngine;
